/**
 * Landing Page Theme Configuration
 *
 * Design tokens and styling for the public institutional website.
 * Based on Clinafy brand colors with extended palette for marketing pages.
 */

// ============================================================================
// Color Tokens
// ============================================================================

export const landingColors = {
  // Primary Brand Colors
  primary: {
    main: '#1B6A63',
    dark: '#145550',
    light: '#2E8C80',
    contrast: '#FFFFFF',
  },

  // Secondary Colors
  secondary: {
    main: '#2E8C80',
    dark: '#1B6A63',
    light: '#4DA89C',
    contrast: '#FFFFFF',
  },

  // Accent Colors
  accent: {
    yellow: '#FFB800',
    orange: '#FF8A65',
    teal: '#27ab83',
  },

  // Neutral Colors
  neutral: {
    white: '#FFFFFF',
    gray50: '#F9FAFB',
    gray100: '#F3F4F6',
    gray200: '#E5E7EB',
    gray300: '#D1D5DB',
    gray400: '#9CA3AF',
    gray500: '#6B7280',
    gray600: '#4B5563',
    gray700: '#374151',
    gray800: '#1F2937',
    gray900: '#111827',
    black: '#000000',
  },

  // Status Colors
  status: {
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
  },

  // Background Colors
  background: {
    default: '#FFFFFF',
    alt: '#F9FAFB',
    dark: '#1F2937',
    gradient: 'linear-gradient(135deg, #1B6A63 0%, #2E8C80 100%)',
    gradientDark: 'linear-gradient(135deg, #145550 0%, #1B6A63 100%)',
  },
};

// ============================================================================
// Gradient Definitions
// ============================================================================

export const landingGradients = {
  hero: 'linear-gradient(135deg, #1B6A63 0%, #2E8C80 100%)',
  heroDark: 'linear-gradient(135deg, #145550 0%, #1B6A63 100%)',
  cta: 'linear-gradient(135deg, #1B6A63 0%, #145550 100%)',
  card: 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)',
  feature: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)',
  pricing: 'linear-gradient(180deg, #1B6A63 0%, #145550 100%)',
};

// ============================================================================
// Shadow Definitions
// ============================================================================

export const landingShadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  card: '0 4px 20px rgba(0, 0, 0, 0.08)',
  cardHover: '0 8px 30px rgba(0, 0, 0, 0.12)',
  button: '0 4px 14px rgba(27, 106, 99, 0.4)',
  buttonHover: '0 6px 20px rgba(27, 106, 99, 0.5)',
};

// ============================================================================
// Typography Tokens
// ============================================================================

export const landingTypography = {
  fontFamily: {
    primary: "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    heading: "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'Fira Code', 'Consolas', monospace",
  },

  fontSize: {
    xs: '0.75rem',     // 12px
    sm: '0.875rem',    // 14px
    base: '1rem',      // 16px
    lg: '1.125rem',    // 18px
    xl: '1.25rem',     // 20px
    '2xl': '1.5rem',   // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem',  // 36px
    '5xl': '3rem',     // 48px
    '6xl': '3.75rem',  // 60px
    '7xl': '4.5rem',   // 72px
  },

  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  lineHeight: {
    tight: 1.1,
    snug: 1.25,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },

  // Pre-defined text styles
  styles: {
    heroTitle: {
      fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    heroSubtitle: {
      fontSize: { xs: '1.125rem', md: '1.25rem' },
      fontWeight: 400,
      lineHeight: 1.6,
    },
    sectionTitle: {
      fontSize: { xs: '2rem', md: '2.5rem' },
      fontWeight: 600,
      lineHeight: 1.2,
    },
    sectionSubtitle: {
      fontSize: { xs: '1rem', md: '1.125rem' },
      fontWeight: 400,
      lineHeight: 1.6,
    },
    cardTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    small: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
  },
};

// ============================================================================
// Spacing Tokens
// ============================================================================

export const landingSpacing = {
  // Base spacing scale (rem)
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
  24: '6rem',     // 96px
  32: '8rem',     // 128px

  // Section spacing
  section: {
    xs: '3rem',    // 48px
    sm: '4rem',    // 64px
    md: '6rem',    // 96px
    lg: '8rem',    // 128px
  },

  // Container max-widths
  container: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};

// ============================================================================
// Border Radius Tokens
// ============================================================================

export const landingBorderRadius = {
  none: '0',
  sm: '0.25rem',    // 4px
  md: '0.5rem',     // 8px
  lg: '0.75rem',    // 12px
  xl: '1rem',       // 16px
  '2xl': '1.5rem',  // 24px
  '3xl': '2rem',    // 32px
  full: '9999px',
  pill: '50px',
};

// ============================================================================
// Animation Tokens
// ============================================================================

export const landingAnimations = {
  duration: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
  },
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
};

// ============================================================================
// Breakpoints
// ============================================================================

export const landingBreakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// ============================================================================
// Z-Index Scale
// ============================================================================

export const landingZIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// ============================================================================
// Export Default Theme
// ============================================================================

const landingTheme = {
  colors: landingColors,
  gradients: landingGradients,
  shadows: landingShadows,
  typography: landingTypography,
  spacing: landingSpacing,
  borderRadius: landingBorderRadius,
  animations: landingAnimations,
  breakpoints: landingBreakpoints,
  zIndex: landingZIndex,
};

export default landingTheme;
